/**
 * Persist a guest visitor's recent orders per restaurant in localStorage.
 * Keeps the "Previous orders" list visible after a refresh — react-query's
 * in-memory cache alone would be wiped.
 */

import type { OrderResponse } from '../types/checkout';

const STORAGE_PREFIX = 'restroos:previous_orders:';
const MAX_ORDERS = 10;
const STALE_DAYS = 1;

interface StoredEntry {
  order: OrderResponse;
  savedAt: number;
}

function getKey(restaurantId: string): string {
  return `${STORAGE_PREFIX}${restaurantId}`;
}

function isFresh(entry: StoredEntry): boolean {
  const ageDays = (Date.now() - (entry.savedAt ?? 0)) / (24 * 60 * 60 * 1000);
  return ageDays <= STALE_DAYS;
}

export function readPreviousOrders(restaurantId: string | undefined | null): OrderResponse[] {
  if (!restaurantId || typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(getKey(restaurantId));
    if (!raw) return [];
    const entries = JSON.parse(raw) as StoredEntry[];
    if (!Array.isArray(entries)) return [];
    return entries.filter(isFresh).map((e) => e.order);
  } catch {
    return [];
  }
}

export function savePreviousOrder(
  restaurantId: string | undefined | null,
  order: OrderResponse,
): void {
  if (!restaurantId || !order?.orderId || typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(getKey(restaurantId));
    const existing = raw ? (JSON.parse(raw) as StoredEntry[]) : [];
    const deduped = (Array.isArray(existing) ? existing : [])
      .filter(isFresh)
      .filter((e) => e.order?.orderId !== order.orderId);
    const next: StoredEntry[] = [{ order, savedAt: Date.now() }, ...deduped].slice(0, MAX_ORDERS);
    localStorage.setItem(getKey(restaurantId), JSON.stringify(next));
  } catch {
    // localStorage full / disabled — surface nothing; cache still works in-memory
  }
}

export function clearPreviousOrders(restaurantId: string | undefined | null): void {
  if (!restaurantId || typeof window === 'undefined') return;
  try {
    localStorage.removeItem(getKey(restaurantId));
  } catch {
    // ignore
  }
}
